import React, { useState } from 'react';
import { SwipeLeft } from '@mui/icons-material';
import { Typography } from '@mui/material';

import { NON_PROPOSED_APPLICATION_TEXT } from './lang';
import { useRejectNonProposedApplication } from '../../../graphql/DRCAssessment/DRCMonitorList/useRejectNonProposedApplication';

import { Alert, type AlertDetails, Dialog } from '../../../../../components';
import {
  ApplicationViewList,
  type ApplicationViewListProps,
  type FilterFields,
  type TableColumns,
} from '../../../../Application/components';
import { type TickActions } from '../../../../Application/components/ApplicationViewList/types';
import { COMMON_ACTION } from '../../../../Application/components/ApplicationViewList/constants';

export type NonProposedListProps = {
  filter?: any;
};

export const NonProposedList: React.FunctionComponent<NonProposedListProps> = (
  props: NonProposedListProps,
) => {
  const { filter: listFilter } = props;

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const [clearTickedHistory, setClearTickedHistory] = useState<boolean>(false);
  const [rejectDialog, setRejectDialog] = useState<{
    open: boolean;
    rejectApplicationIds: string[];
  }>({
    open: false,
    rejectApplicationIds: [],
  });

  const rejectNonProposed = useRejectNonProposedApplication();
  const handleDialogConfirmAction = async (): Promise<void> => {
    const applicationIds = rejectDialog.rejectApplicationIds;

    try {
      await rejectNonProposed({
        variables: {
          applicationIds,
        },
      });
      setAlertDetails({
        alertContent: 'Rejected Successfully',
        alertType: 'success',
      });
      setClearTickedHistory(!clearTickedHistory);
    } catch (error) {
      setAlertDetails({
        alertContent: 'Failed to Reject' + (error as Error)?.message,
        alertType: 'error',
      });
    } finally {
      setOpenAlert(true);
      closeDialog();
    }
  };

  const handleOpenConfirmDialog = (applicationIds: string[]): void => {
    if (!applicationIds.length) return;
    setRejectDialog({
      open: true,
      rejectApplicationIds: applicationIds,
    });
  };
  const closeDialog = (): void => {
    setRejectDialog({
      open: false,
      rejectApplicationIds: [],
    });
  };
  const { open } = rejectDialog;

  const hiddenFilters: FilterFields[] = React.useMemo(
    () => [
      'department',
      'applicantIdentityType',
      'applicantLocalIndicator',
      'scholarshipAppliedByApplicant',
      'applicationStatus',
      'fundingSchemeStatus',
      'fundingScheme',
      'hkpfsNo',
      'dualPartnerUniversity',
      'jointpartnerUniversity',
      'partnerCategory',
      'gsAssignee',
    ],
    [],
  );

  const tickActions: TickActions[] = [
    {
      icon: <SwipeLeft />,
      label: 'Reject',
      handleAction: handleOpenConfirmDialog,
    },
  ];

  const visibleColumns: TableColumns[] = React.useMemo(
    () => [
      'applicationNo',
      'applicantName',
      'applicantEmail',
      'department',
      'applicantNationality',
      'entryYear',
      'applyProgramme',
      'researchAreas',
      'applicationStatus',
      'applicationSubmissionTime',
    ],
    [],
  );

  const filterProps: ApplicationViewListProps['filterProps'] = {
    hiddenFilterFields: hiddenFilters,
  };

  const toolsProps: ApplicationViewListProps['toolsProps'] = {
    enableExport: false,
    tickActions,
  };
  const tableProps: ApplicationViewListProps['tableProps'] = {
    initialVisibleColumns: visibleColumns,
    listFilter,
    pickCommonActions: [COMMON_ACTION.MONITOR, COMMON_ACTION.VIEW_AS],
  };

  return (
    <div>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <Dialog
        openDialog={open}
        dialogTitle={NON_PROPOSED_APPLICATION_TEXT.REJECT.DIALOG.TITLE}
        dialogAction={NON_PROPOSED_APPLICATION_TEXT.REJECT.DIALOG.CONFIRM}
        cancelAction={NON_PROPOSED_APPLICATION_TEXT.REJECT.DIALOG.CANCEL}
        onDialogAction={handleDialogConfirmAction}
        onDialogClose={closeDialog}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {NON_PROPOSED_APPLICATION_TEXT.REJECT.DIALOG.CONTENT}
        </Typography>
      </Dialog>

      <ApplicationViewList
        key={Number(clearTickedHistory)}
        tableProps={tableProps}
        filterProps={filterProps}
        toolsProps={toolsProps}
      />
    </div>
  );
};
